import { Controller } from "@hotwired/stimulus"
import {
  DEFAULT_LOCALE,
  formatNumber,
  getCurrencyDecimals,
  getUserLocale,
  MACHINE_NUMBER,
  parseInputNumber,
  parseLocaleNumber
} from "../../utils/number-format.util"
import { currencyFormatFor, formatAmount, formatDecimal, serverNumberFormatFor } from "../../utils/server-format.util"

const DEFAULT_FRACTION_DIGITS = 2

// Connects to data-controller="documents--number-format"
export default class extends Controller {
  static values = {
    style: { type: String, default: "decimal" },
    currency: {
      type: String,
      default: "EUR"
    },
    minimumFractionDigits: { type: Number, default: DEFAULT_FRACTION_DIGITS },
    // Declared WITHOUT `default:` on purpose. Stimulus computes
    // `hasXValue` as `data.has(key) || hasCustomDefaultValue`, so declaring a
    // default makes `hasXValue` permanently true and destroys our ability to
    // distinguish "the template explicitly asked for N digits" from "nothing
    // was specified". The VND test in the spec file fails if you
    // re-add a default here.
    maximumFractionDigits: Number,
    // Opt-in: when true, fraction digits come from the currency (VND=0, EUR=2, BHD=3).
    // Used for monetary-amount inputs that mirror Ruby's currency_decimals.
    // See docs/VAT_ROUNDING_POLICY.md §9.
    currencyDrivenPrecision: { type: Boolean, default: false },
    // A PRINTED value (not an <input>) takes this locale — the PDF passes the
    // document's regional format (documents.pdf_locale). An input is always
    // parsed and re-formatted in the typing user's meta[name="user-locale"].
    locale: String
  }

  elementTagName = "INPUT"

  connect() {
    this.#onDocumentCurrencyChange()

    this.elementTagName = this.element.tagName

    // Cache the locale from form or use provided value
    this.#cacheLocale()
    // A printed cell replays the server's format (ApplicationHelper#number_format_data:
    // the document's regional format in a PDF — documents/pdf/create.html.erb —
    // the user's on the document form) so it reads exactly like the
    // server-printed totals beside it. Intl's CLDR data differs from
    // rails-i18n's for the same region (pl and es do not group 4 digits,
    // de-CH groups with ’, en-IN groups by lakh, ar-SA prints Arabic-Indic
    // digits). Inputs keep Intl: they parse what a user types.
    this.serverFormat = this.elementTagName !== "INPUT" ? serverNumberFormatFor(this.element) : null

    // Format immediately with user's locale (no need to wait for events)
    // On initial load, parse with the same locale since it's static
    this.formatNumber()
  }

  // Cached once: the locale cannot change during the controller's life.
  #cacheLocale() {
    const printed = this.elementTagName !== "INPUT"
    this.cachedLocale = printed && this.localeValue ? this.localeValue : getUserLocale()
  }

  disconnect() {
    if (this.currencyChangeHandler && this.documentCurrency) {
      this.documentCurrency.removeEventListener("change", this.currencyChangeHandler)
    }
  }

  formatNumber() {
    const value = this.#getValue()
    if (!value) return

    if (this.serverFormat) {
      // Only the server's raw decimal is formatted; anything else already is.
      if (MACHINE_NUMBER.test(value)) this.#setValue(this.#formatLikeServer(Number(value)))
      return
    }

    const options = this.#buildOptions()
    // Use cached locale (from provided value or meta tag) with default fallback
    const locale = this.cachedLocale || DEFAULT_LOCALE

    // Parse with user's profile locale and format with the same locale
    // Since locale is static, we use the same locale for both parsing and formatting
    const rawNumber = this.#parse(value, locale)
    if (rawNumber === null) return // Invalid number, skip formatting

    // Check if value is already correctly formatted to prevent double-formatting
    // This happens when invoice-item controller sets formatted values on fields with number-format controllers
    const expectedFormatted = formatNumber(rawNumber, locale, options)
    if (value === expectedFormatted) {
      return // Already correctly formatted, skip to prevent double-formatting
    }

    // Format the number
    this.#setValue(expectedFormatted)
  }

  // A printed cell, and an input the user has not touched, start as the
  // server's raw decimal ("1234.5") — a machine number, never a localized one:
  // read through a German locale, whose "." is the thousands separator, it
  // would become 12345 on the invoice (parseInputNumber).
  #parse(value, locale) {
    if (this.elementTagName === "INPUT") return parseInputNumber(this.element, locale)
    if (MACHINE_NUMBER.test(value)) return Number(value)
    return parseLocaleNumber(value, locale)
  }

  // number_to_currency / number_with_precision / number_to_percentage as the
  // server prints them, with the same fraction-digit rules as #buildOptions:
  // a currency's own decimals at least, more only up to an explicit maximum.
  #formatLikeServer(number) {
    const numberFormat = this.serverFormat
    if (this.styleValue === "currency") {
      const code = this.hasCurrencyValue ? this.currencyValue : "EUR"
      const decimals = getCurrencyDecimals(code)
      const maximum = this.hasMaximumFractionDigitsValue ? Math.max(this.maximumFractionDigitsValue, decimals) : decimals
      const precision = this.#fractionDigits(number, decimals, maximum)
      return formatAmount(number, { ...currencyFormatFor(code, numberFormat), precision })
    }

    const digits = {
      minimumFractionDigits: this.minimumFractionDigitsValue,
      maximumFractionDigits: this.hasMaximumFractionDigitsValue
        ? this.maximumFractionDigitsValue
        : DEFAULT_FRACTION_DIGITS
    }
    // Same rule as #buildOptions: the currency's minor unit fixes both ends.
    if (this.currencyDrivenPrecisionValue && this.hasCurrencyValue) {
      const decimals = getCurrencyDecimals(this.currencyValue)
      digits.minimumFractionDigits = decimals
      digits.maximumFractionDigits = decimals
    }
    const text = formatDecimal(number, numberFormat, digits)
    if (this.styleValue !== "percent") return text
    return (numberFormat.percentage_format || "%n%").replace("%n", text)
  }

  // The digits a value needs between minimum and maximum: 12.5 → 2 for EUR,
  // 0.12345 → 5 with a maximum of 6.
  #fractionDigits(number, minimum, maximum) {
    const fraction = Math.abs(number).toFixed(maximum).split(".")[1] || ""
    return Math.max(minimum, fraction.replace(/0+$/, "").length)
  }

  #buildOptions() {
    const options = {
      style: this.styleValue,
      currency: this.hasCurrencyValue ? this.currencyValue : undefined
    }
    if (this.styleValue === "currency") {
      // formatNumber takes fraction digits from our currency table (VND: 0,
      // EUR: 2, BHD: 3, HUF: 2) — never from the browser's CLDR, which gives
      // HUF 0 in Chrome 153. Hardcoded min/max here would override that.
      // See docs/VAT_ROUNDING_POLICY.md §3, §9.
      //
      // Exception: a template may explicitly widen the MAXIMUM. EN 16931 caps
      // BT-131 (line net) and BT-112 (total) at 2 decimals via BR-DEC-23 and
      // BR-DEC-14, but places no cap on BT-146 (item net price). The currency
      // still supplies the minimum, so ordinary prices keep their 2 decimals
      // and only sub-cent unit prices grow digits.
      if (this.hasMaximumFractionDigitsValue) {
        options.maximumFractionDigits = this.maximumFractionDigitsValue
      }
      return options
    }
    options.minimumFractionDigits = this.minimumFractionDigitsValue
    options.maximumFractionDigits = this.hasMaximumFractionDigitsValue
      ? this.maximumFractionDigitsValue
      : DEFAULT_FRACTION_DIGITS
    if (this.currencyDrivenPrecisionValue && this.hasCurrencyValue) {
      const decimals = getCurrencyDecimals(this.currencyValue)
      options.minimumFractionDigits = decimals
      options.maximumFractionDigits = decimals
    }
    return options
  }

  #getValue() {
    return this.elementTagName === "INPUT" ? this.element.value : this.element.innerText
  }

  #setValue(value) {
    if (this.elementTagName === "INPUT") {
      this.element.value = value
    } else {
      this.element.innerText = value
    }
  }

  #onDocumentCurrencyChange() {
    const documentCurrency = document.querySelector("#currency")
    this.documentCurrency = documentCurrency

    if (documentCurrency) {
      this.currencyValue = documentCurrency.value
      this.currencyChangeHandler = (event) => {
        this.currencyValue = event.target.value
        // Re-apply precision (currency-driven mode) after the select changes,
        // so a field editing a VND expense shrinks from 2 decimals to 0 live.
        if (this.currencyDrivenPrecisionValue) {
          this.formatNumber()
        }
      }
      documentCurrency.addEventListener("change", this.currencyChangeHandler)
    }
  }
}
